import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/system';
import { useAuth } from '../../context/AuthContext';
import AppColors from '../../utils/AppColors';

// Use the same pattern as vendor login
const patternImage = '/images/Pattern.png';

const LoginContainer = styled(Box)({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: `url(${patternImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        zIndex: 1,
    }
});

const LoginCard = styled(Paper)({
    padding: '3rem',
    borderRadius: '25px', // Match vendor border radius
    boxShadow: '0 15px 35px rgba(0,0,0,0.1)', // Match vendor shadow
    width: '100%',
    maxWidth: '450px', // Match vendor width
    position: 'relative',
    zIndex: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
});

const Logo = styled('img')({
    width: '140px', // Match vendor logo size
    marginBottom: '2rem',
    display: 'block',
    margin: '0 auto 2rem auto',
});

const Title = styled(Typography)({
    fontSize: '2rem', // Match vendor title size
    fontWeight: 'bold',
    color: AppColors.primaryDark,
    textAlign: 'center',
    marginBottom: '0.5rem',
});

const Subtitle = styled(Typography)({
    fontSize: '1rem',
    color: AppColors.textSecondary,
    textAlign: 'center',
    marginBottom: '2.5rem',
});

const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: '15px',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
        },
        '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
        },
    },
});

const LoginButton = styled(Button)({
    borderRadius: '25px',
    height: '55px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    backgroundColor: AppColors.primaryDark,
    '&:hover': {
        backgroundColor: AppColors.primaryYellow,
        color: AppColors.primaryDark,
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
    },
    transition: 'all 0.3s ease',
});

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const result = await login(email, password);
        
        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    return (
        <LoginContainer>
            <LoginCard>
                <Logo src="/jaddid-logo.png" alt="Jaddid Admin" />
                <Title>Admin Portal</Title>
                <Subtitle>Comprehensive management dashboard</Subtitle>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '15px' }}>
                        {error}
                    </Alert>
                )}
                
                <Box component="form" onSubmit={handleSubmit}>
                    <StyledTextField
                        fullWidth
                        label="Admin Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        margin="normal"
                        required
                        sx={{ mb: 2 }}
                    />
                    
                    <StyledTextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        margin="normal"
                        required
                        sx={{ mb: 3 }}
                    />
                    
                    <LoginButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Access Admin Portal'}
                    </LoginButton>
                </Box>
            </LoginCard>
        </LoginContainer>
    );
};

export default Login;
